import { BreakdownChart } from '@/components/charts/breakdownChart'
import { ScrollArea } from '@/components/common/scrollArea'
import { calculateAverageApy } from '@/components/dashboard/useGroupedBalances/calculateAverageApy'
import { useBreakdownItems } from '@/hooks/dashboard/useBreakdownItems'
import type { BreakdownItem } from '@/types/analytics'
import type { VaultApyRange } from '@/types/apy'
import { getApyRangeCopy } from '@/utils/apy'
import { getBreakdownItemColor } from '@/utils/chartUtils/breakdownItemColor'
import { cn } from '@/utils/cn'
import { formatDecimalToPercentage, formatUSD } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { BlurCover } from '../common/blurCover'
import { StatisticsTile } from '../common/statisticsTile'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../common/tabs'
import type { LpTokensBalanceGroup } from './useGroupedBalances/useGroupedPositions'

interface Props {
  lpTokensBalance: LpTokensBalanceGroup
  apyRange: VaultApyRange
  breakdownTab: BreakdownTab
  onBreakdownTabChange: (tab: BreakdownTab) => void
  loading?: boolean
}

const BREAKDOWN_TABS = [
  { key: 'asset', label: 'Asset' },
  { key: 'protocol', label: 'Protocol' },
  { key: 'network', label: 'Network' },
] as const

export type BreakdownTab = (typeof BREAKDOWN_TABS)[number]['key']

export const BREAKDOWN_TAB_VALUES = BREAKDOWN_TABS.map(({ key }) => key) as readonly BreakdownTab[]

export const DEFAULT_BREAKDOWN_TAB: BreakdownTab = 'asset'

export function ActivePositionsChart({
  lpTokensBalance,
  apyRange,
  breakdownTab,
  onBreakdownTabChange,
  loading,
}: Props) {
  const totalValue = useMemo(
    () => lpTokensBalance.reduce((acc, { groupTotalBalance }) => acc + groupTotalBalance, 0),
    [lpTokensBalance]
  )

  const averageApy = useMemo(
    () =>
      calculateAverageApy(
        lpTokensBalance.map(({ groupTotalBalance, groupApy }) => ({ balanceUsd: groupTotalBalance, apy: groupApy }))
      ),
    [lpTokensBalance]
  )

  const assetItems = useBreakdownItems(lpTokensBalance, 'asset', totalValue)
  const protocolItems = useBreakdownItems(lpTokensBalance, 'protocol', totalValue)
  const networkItems = useBreakdownItems(lpTokensBalance, 'network', totalValue)

  const tabItems = { asset: assetItems, protocol: protocolItems, network: networkItems }

  return (
    <Tabs
      value={breakdownTab}
      onValueChange={(value) => onBreakdownTabChange(value as BreakdownTab)}
      className={cn('relative flex flex-col gap-4', loading && 'animate-pulse')}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-2xl border bg-surface py-2 pr-2 pl-4 md:pl-5">
        <h2 className="font-semibold text-heading-foreground text-lg">Active positions</h2>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-body-foreground">Breakdown by</span>
          <TabsList className="rounded-xl bg-page p-1">
            {BREAKDOWN_TABS.map(({ key, label }) => (
              <TabsTrigger
                key={key}
                value={key}
                className="h-6 rounded-lg data-[state=active]:border-accent data-[state=active]:bg-accent data-[state=active]:hocus:text-button-foreground data-[state=active]:text-button-foreground"
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-[3fr_5fr]">
        <div className="flex flex-wrap gap-4 lg:flex-col">
          <StatisticsTile
            label="Active positions value"
            value={formatUSD(totalValue)}
            className="flex-1 rounded-2xl p-4 md:p-5 md:py-4"
          />
          <StatisticsTile
            label={`Avg. APY (${getApyRangeCopy(apyRange).compact})`}
            value={formatDecimalToPercentage(averageApy)}
            className="flex-1 rounded-2xl p-4 md:p-5 md:py-4"
          />
        </div>
        <div className="flex flex-col rounded-2xl border bg-surface">
          {BREAKDOWN_TABS.map(({ key }) => (
            <TabsContent key={key} value={key} className="flex flex-grow flex-col">
              <BreakdownTabContent items={tabItems[key]} />
            </TabsContent>
          ))}
        </div>
      </div>
      {loading && <BlurCover className="bg-transparent" />}
    </Tabs>
  )
}

const BreakdownLegend = ({ items }: { items: BreakdownItem[] }) => {
  return (
    <ul className="flex min-w-0 flex-1 flex-col gap-1.5">
      {items.map((item, index) => (
        <li key={item.name} className="flex items-center gap-2 text-xs">
          <span
            className="size-2.5 min-w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: getBreakdownItemColor(item, index) }}
          />
          <span className="min-w-0 flex-1 truncate text-body-foreground" title={item.displayName}>
            {item.displayName}
          </span>
          <span className="shrink-0 text-body-foreground tabular-nums">
            {formatUSD(item.value)}{' '}
            <span className="text-body-foreground/60">({formatDecimalToPercentage(item.percentage)})</span>
          </span>
        </li>
      ))}
    </ul>
  )
}

const BreakdownTabContent = ({ items }: { items: BreakdownItem[] }) => {
  return (
    <div className="flex flex-grow">
      <div className="pt-4 pl-4 md:pl-5">
        <BreakdownChart items={items} size={128} className="size-32 min-w-32" />
      </div>
      <ScrollArea type="auto" className="h-40 flex-grow">
        <div className="pt-4 pr-4 pb-4 pl-4 md:pr-5 md:pb-5">
          <BreakdownLegend items={items} />
        </div>
      </ScrollArea>
    </div>
  )
}
